import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Combobox } from '../ui/combobox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Loader2, Pencil, Undo2 } from 'lucide-react';
import { toast } from 'sonner';

const DispensacaoCorrecaoDialog = ({ registro, estoqueItens, onClose, onSaved }) => {
  const { api } = useAuth();
  const [form, setForm] = useState({ item_id: '', quantidade: '', observacoes: '', motivo: '' });
  const [saving, setSaving] = useState(null);
  const [confirmEstorno, setConfirmEstorno] = useState(false);

  useEffect(() => {
    if (registro) {
      setForm({ item_id: registro.item_id || '', quantidade: String(registro.quantidade ?? ''), observacoes: registro.observacoes || '', motivo: '' });
      setConfirmEstorno(false);
    }
  }, [registro]);

  if (!registro) return null;
  const motivoOk = form.motivo.trim().length >= 5;
  const options = [...estoqueItens.filter(i => i.id !== registro.item_id).map(i => ({ value: i.id, label: `${i.nome} (${i.estoque_atual} ${i.unidade})`, subtitle: i.tipo === 'medicamento' ? 'Medicamento' : 'Material' }))];
  if (registro.item_id) options.unshift({ value: registro.item_id, label: registro.item_nome, subtitle: 'Item atual' });

  const corrigir = async () => {
    const qtd = parseFloat(form.quantidade);
    if (!qtd || qtd <= 0) return toast.error('Quantidade inválida');
    setSaving('corrigir');
    try {
      await api.put(`/dispensacao/${registro.dispensacao_id}/corrigir`, { item_id: form.item_id || undefined, quantidade: qtd, observacoes: form.observacoes, motivo: form.motivo.trim() });
      toast.success('Dispensação corrigida'); onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao corrigir'); } finally { setSaving(null); }
  };
  const estornar = async () => {
    setSaving('estornar');
    try {
      await api.post(`/dispensacao/${registro.dispensacao_id}/estornar`, { motivo: form.motivo.trim() });
      toast.success('Dispensação estornada — estoque devolvido'); onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao estornar'); } finally { setSaving(null); }
  };

  return (
    <Dialog open={!!registro} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="dialog-corrigir-dispensacao">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="h-4 w-4" /> Corrigir dispensação</DialogTitle>
          <DialogDescription>{registro.item_nome} · {registro.data ? new Date(registro.data + 'T12:00:00').toLocaleDateString('pt-BR') : ''}{registro.paciente_nome ? ` · ${registro.paciente_nome}` : ''}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="space-y-1">
            <Label className="text-sm">Item</Label>
            <Combobox options={options} value={form.item_id} onValueChange={(v) => setForm(f => ({ ...f, item_id: v }))} placeholder="Buscar item..." searchPlaceholder="Digite o nome..." emptyText="Nenhum item" data-testid="select-corrigir-item" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1">
              <Label className="text-sm">Quantidade</Label>
              <Input type="number" step="0.5" min="0.5" value={form.quantidade} onChange={(e) => setForm(f => ({ ...f, quantidade: e.target.value }))} data-testid="input-corrigir-quantidade" />
            </div>
            <div className="col-span-2 space-y-1">
              <Label className="text-sm">Observação</Label>
              <Input value={form.observacoes} onChange={(e) => setForm(f => ({ ...f, observacoes: e.target.value }))} data-testid="input-corrigir-observacoes" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-sm">Motivo da correção/estorno *</Label>
            <Textarea rows={2} value={form.motivo} onChange={(e) => setForm(f => ({ ...f, motivo: e.target.value }))} placeholder="Ex.: item dispensado para o paciente errado" data-testid="input-corrigir-motivo" />
          </div>
          {confirmEstorno && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="estorno-confirm-text">
              O item será removido da dispensação e do custo do paciente e o estoque devolvido. O registro fica guardado no histórico. Confirmar?
            </p>
          )}
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2 sm:justify-between">
          {confirmEstorno ? (
            <Button variant="destructive" onClick={estornar} disabled={!motivoOk || !!saving} data-testid="btn-confirmar-estorno">
              {saving === 'estornar' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Undo2 className="h-4 w-4 mr-1" />} Confirmar estorno
            </Button>
          ) : (
            <Button variant="outline" className="text-red-700 border-red-300 hover:bg-red-50" onClick={() => setConfirmEstorno(true)} disabled={!motivoOk || !!saving} data-testid="btn-estornar">
              <Undo2 className="h-4 w-4 mr-1" /> Excluir (estornar)
            </Button>
          )}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={!!saving} data-testid="btn-cancelar-correcao">Cancelar</Button>
            <Button onClick={corrigir} disabled={!motivoOk || !!saving} data-testid="btn-salvar-correcao">
              {saving === 'corrigir' && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Salvar correção
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DispensacaoCorrecaoDialog;
